import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, LogOut, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../services/api';
import { AdminSidebar } from '../components/admin/AdminSidebar';
import { CreateUserForm } from '../components/admin/CreateUserForm';
import { UserListPagination } from '../components/admin/UserListPagination';
import { UserListTable } from '../components/admin/UserListTable';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const DEFAULT_PAGE_SIZE = 10;

const emptyForm = {
  name: '',
  email: '',
  password: '',
  phone: '',
  role: 'dealing_assistant',
};

export default function UserManagementPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async (nextPage, nextPageSize) => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getUsers({ page: nextPage, page_size: nextPageSize });
      setUsers(Array.isArray(data?.users) ? data.users : []);
      setTotal(data?.total ?? 0);
      setPage(data?.page ?? nextPage);
      setTotalPages(data?.total_pages ?? 1);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(page, pageSize);
  }, [load, page, pageSize]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminAPI.createUser(form);
      toast.success('User created');
      setForm(emptyForm);
      if (page === 1) {
        await load(1, pageSize);
      } else {
        setPage(1);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="user-management-page">
      <div className="tricolor-strip" />
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="h-6 w-6 text-[#003380]" />
            <div>
              <h1 className="text-xl font-bold govt-blue-text font-serif">User Management</h1>
              <p className="text-sm text-slate-600">Department accounts</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right mr-1">
              <p className="text-sm font-medium text-slate-800">{user?.name}</p>
              <p className="text-xs text-slate-500 capitalize">
                {user?.role?.replace(/_/g, ' ')}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                logout();
                navigate('/');
              }}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        <AdminSidebar />
        <main className="flex-1 p-6 space-y-6">
          <Card className="govt-card">
            <CardHeader>
              <CardTitle className="text-lg">Create department user</CardTitle>
            </CardHeader>
            <CardContent>
              <CreateUserForm
                form={form}
                setForm={setForm}
                saving={saving}
                onSubmit={handleCreate}
              />
            </CardContent>
          </Card>

          <Card className="govt-card">
            <CardHeader>
              <CardTitle className="text-lg">Users</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[#003380]" />
                </div>
              ) : (
                <>
                  <UserListTable users={users} />
                  <UserListPagination
                    total={total}
                    page={page}
                    pageSize={pageSize}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setPage(1);
                    }}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
}
